import {
  calculateBuyerGroupAmount,
  createBuyerPurchasedGroup,
  getBuyerGroupSeatBounds,
  normalizeBuyerTicketConfiguration,
  projectBuyerTicketPurchase,
  resizeBuyerPurchasedGroup,
} from '@charitystack/shared/group-tickets/buyer';
import { GROUP_TICKET_ERROR_CODES } from '@charitystack/shared/group-tickets';

// Upper bound on a prefilled individual-ticket count; mirrors the public API's
// prefill.ticketQuantity cap (public-api-backend link_utils.MAX_PREFILL_ATTENDEES).
export const MAX_PREFILLED_TICKET_QUANTITY = 50;

export const TICKET_AVAILABILITY_CHANGED_MESSAGE =
  'Ticket availability changed. Please reduce or remove the unavailable selection.';

const recoverableAvailabilityCodes = new Set([
  GROUP_TICKET_ERROR_CODES.GROUP_INVENTORY_UNAVAILABLE,
  GROUP_TICKET_ERROR_CODES.SEAT_INVENTORY_UNAVAILABLE,
]);

export const projectBuyerTicketPurchaseSafely = (input) => {
  try {
    return { purchase: projectBuyerTicketPurchase(input), error: null };
  } catch (error) {
    if (!recoverableAvailabilityCodes.has(error?.code)) throw error;
    return { purchase: null, error: TICKET_AVAILABILITY_CHANGED_MESSAGE };
  }
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const recalculate = (ticket, ticketContractVersion) => {
  const normalized = normalizeBuyerTicketConfiguration(ticket, ticketContractVersion);
  if (ticketContractVersion === 2 && normalized.type === 'GROUP') {
    const groups = ticket.selectedGroups ?? [];
    ticket.soldNow = groups.length;
    ticket.soldNowPrice = groups.reduce(
      (sum, group) =>
        sum + calculateBuyerGroupAmount(ticket, group.seatCount, ticketContractVersion),
      0,
    );
    return ticket;
  }
  if (normalized.type === 'SINGLE') {
    ticket.soldNow = (ticket.attendees ?? []).length;
    ticket.soldNowPrice = ticket.soldNow * Number(ticket.price || 0);
    return ticket;
  }
  const seats = Number(normalized.seats);
  ticket.soldNow = (ticket.attendees ?? []).length / seats;
  ticket.soldNowPrice = ticket.soldNow * Number(ticket.price || 0);
  return ticket;
};

export const initializeBuyerTicket = (ticket, ticketContractVersion) => ({
  ...ticket,
  quantity: Number.parseInt(ticket.quantity, 10) || 0,
  sold: Number.parseInt(ticket.sold, 10) || 0,
  soldNow: 0,
  soldNowPrice: 0,
  attendees: [],
  ...(ticketContractVersion === 2 ? { selectedGroups: [] } : {}),
});

export const refreshBuyerTicketsFromConfiguration = ({
  existingTickets = [],
  freshTickets = [],
  ticketContractVersion,
  initialized,
  previewMode,
}) => {
  if (!initialized || previewMode) {
    return {
      tickets: freshTickets.map((ticket) => initializeBuyerTicket(ticket, ticketContractVersion)),
      resetSelection: Boolean(initialized && previewMode),
    };
  }

  return {
    tickets: existingTickets.map((existingTicket) => {
      const freshTicket = freshTickets.find((ticket) => ticket.id === existingTicket.id);
      if (!freshTicket) return existingTicket;
      return {
        ...existingTicket,
        quantity: Number.parseInt(freshTicket.quantity, 10) || 0,
        sold: Number.parseInt(freshTicket.sold, 10) || 0,
        seatCapacity:
          freshTicket.seatCapacity == null
            ? existingTicket.seatCapacity
            : Number.parseInt(freshTicket.seatCapacity, 10) || 0,
        seatsSold:
          freshTicket.seatsSold == null
            ? existingTicket.seatsSold
            : Number.parseInt(freshTicket.seatsSold, 10) || 0,
        archived: freshTicket.archived === true,
        ticketSalesPaused: freshTicket.ticketSalesPaused === true,
      };
    }),
    resetSelection: false,
  };
};

export const addBuyerTicketUnit = ({
  ticket: input,
  ticketContractVersion,
  purchasedGroupID,
  createAttendee,
}) => {
  const ticket = clone(input);
  const normalized = normalizeBuyerTicketConfiguration(ticket, ticketContractVersion);
  ticket.attendees ??= [];

  if (normalized.type === 'SINGLE') {
    if (ticket.attendees.length >= normalized.quantity - normalized.sold) return input;
    ticket.attendees.push(createAttendee({ ticket, type: 'SINGLE' }));
    return recalculate(ticket, ticketContractVersion);
  }

  if (ticketContractVersion !== 2) {
    const seats = Number(normalized.seats);
    if (ticket.attendees.length / seats >= normalized.quantity - normalized.sold) return input;
    for (let seatIndex = 1; seatIndex <= seats; seatIndex += 1) {
      ticket.attendees.push(createAttendee({ ticket, type: 'GROUP', seats, seatIndex }));
    }
    return recalculate(ticket, ticketContractVersion);
  }

  ticket.selectedGroups ??= [];
  const bounds = getBuyerGroupSeatBounds(ticket, ticketContractVersion);
  const locallySelectedSeats = ticket.selectedGroups.reduce(
    (sum, group) => sum + group.seatCount,
    0,
  );
  if (
    ticket.selectedGroups.length >= bounds.remainingGroups ||
    locallySelectedSeats + bounds.minimum > bounds.remainingSeats
  ) {
    return input;
  }
  const group = createBuyerPurchasedGroup({
    ticket,
    ticketContractVersion,
    purchasedGroupID,
  });
  ticket.selectedGroups.push(group);
  for (let seatIndex = 1; seatIndex <= group.seatCount; seatIndex += 1) {
    ticket.attendees.push(
      createAttendee({
        ticket,
        type: 'GROUP',
        seats: group.seatCount,
        seatIndex,
        purchasedGroupID: group.purchasedGroupID,
      }),
    );
  }
  return recalculate(ticket, ticketContractVersion);
};

export const removeBuyerTicketUnit = ({ ticket: input, ticketContractVersion }) => {
  const ticket = clone(input);
  const normalized = normalizeBuyerTicketConfiguration(ticket, ticketContractVersion);
  if (normalized.type === 'SINGLE') {
    ticket.attendees?.pop();
    return recalculate(ticket, ticketContractVersion);
  }
  if (ticketContractVersion !== 2) {
    ticket.attendees?.splice(-Number(normalized.seats));
    return recalculate(ticket, ticketContractVersion);
  }
  const group = ticket.selectedGroups?.pop();
  if (!group) return input;
  ticket.attendees = (ticket.attendees ?? []).filter(
    (attendee) => attendee.purchasedGroupID !== group.purchasedGroupID,
  );
  return recalculate(ticket, ticketContractVersion);
};

export const resizeBuyerTicketGroup = ({
  ticket: input,
  ticketContractVersion,
  purchasedGroupID,
  seatCount,
  createAttendee,
}) => {
  const ticket = clone(input);
  const groupIndex = (ticket.selectedGroups ?? []).findIndex(
    (group) => group.purchasedGroupID === purchasedGroupID,
  );
  if (ticketContractVersion !== 2 || groupIndex < 0) return input;
  const current = ticket.selectedGroups[groupIndex];
  const otherSelectedSeats = ticket.selectedGroups.reduce(
    (sum, group, index) => sum + (index === groupIndex ? 0 : group.seatCount),
    0,
  );
  const bounds = getBuyerGroupSeatBounds(ticket, ticketContractVersion);
  const localMaximum = Math.min(bounds.maximum, bounds.remainingSeats - otherSelectedSeats);
  if (seatCount < bounds.minimum || seatCount > localMaximum) return input;

  ticket.selectedGroups[groupIndex] = resizeBuyerPurchasedGroup({
    ticket,
    ticketContractVersion,
    purchasedGroup: current,
    seatCount,
  });
  const existing = (ticket.attendees ?? [])
    .filter((attendee) => attendee.purchasedGroupID === purchasedGroupID)
    .sort((a, b) => a.seatIndex - b.seatIndex);
  const retained = existing.slice(0, seatCount).map((attendee, index) => ({
    ...attendee,
    seats: seatCount,
    seatIndex: index + 1,
  }));
  while (retained.length < seatCount) {
    retained.push(
      createAttendee({
        ticket,
        type: 'GROUP',
        seats: seatCount,
        seatIndex: retained.length + 1,
        purchasedGroupID,
      }),
    );
  }
  ticket.attendees = [
    ...(ticket.attendees ?? []).filter(
      (attendee) => attendee.purchasedGroupID !== purchasedGroupID,
    ),
    ...retained,
  ];
  return recalculate(ticket, ticketContractVersion);
};

export const getBuyerTicketAvailability = (ticket, ticketContractVersion) => {
  const normalized = normalizeBuyerTicketConfiguration(ticket, ticketContractVersion);
  if (normalized.type === 'SINGLE') {
    return Math.max(normalized.quantity - normalized.sold - (ticket.attendees ?? []).length, 0);
  }
  const selectedGroups =
    ticketContractVersion === 2
      ? (ticket.selectedGroups ?? []).length
      : (ticket.attendees ?? []).length / normalized.seats;
  const remainingGroups = Math.max(normalized.quantity - normalized.sold - selectedGroups, 0);
  if (ticketContractVersion !== 2) return remainingGroups;
  const bounds = getBuyerGroupSeatBounds(ticket, ticketContractVersion);
  const locallySelectedSeats = (ticket.selectedGroups ?? []).reduce(
    (sum, group) => sum + group.seatCount,
    0,
  );
  return Math.min(
    remainingGroups,
    Math.floor(Math.max(bounds.remainingSeats - locallySelectedSeats, 0) / bounds.minimum),
  );
};

export const applyBuyerTicketPrefill = ({
  ticket,
  ticketContractVersion,
  purchasedGroupID,
  requestedSeatCount,
  createAttendee,
}) => {
  const hasRequestedCount = requestedSeatCount != null && requestedSeatCount !== '';
  const { type } = normalizeBuyerTicketConfiguration(ticket, ticketContractVersion);
  // Individual tickets: the requested count is how many tickets to select
  // (a prefilled link selling N seats to one buyer). All or nothing, so the
  // buyer is never silently handed fewer tickets than the link promised.
  if (type === 'SINGLE' && hasRequestedCount) {
    const parsedTicketCount = Number(requestedSeatCount);
    if (
      !Number.isInteger(parsedTicketCount) ||
      parsedTicketCount < 1 ||
      parsedTicketCount > MAX_PREFILLED_TICKET_QUANTITY
    ) {
      return { ticket, error: 'The requested ticket quantity is invalid' };
    }
    // Check stock up front: the loop below clones per ticket, so it must never
    // be driven by a count the ticket cannot fill.
    if (parsedTicketCount > getBuyerTicketAvailability(ticket, ticketContractVersion)) {
      return { ticket, error: 'The requested ticket quantity is unavailable' };
    }
    let selected = ticket;
    for (let count = 0; count < parsedTicketCount; count += 1) {
      const next = addBuyerTicketUnit({
        ticket: selected,
        ticketContractVersion,
        purchasedGroupID,
        createAttendee,
      });
      if (next === selected) {
        return { ticket, error: 'The requested ticket quantity is unavailable' };
      }
      selected = next;
    }
    return { ticket: selected, error: null };
  }

  const added = addBuyerTicketUnit({
    ticket,
    ticketContractVersion,
    purchasedGroupID,
    createAttendee,
  });
  if (added === ticket) {
    return { ticket, error: 'This ticket is no longer available' };
  }
  if (ticketContractVersion !== 2 || !hasRequestedCount) {
    return { ticket: added, error: null };
  }
  const parsedSeatCount = Number(requestedSeatCount);
  if (!Number.isInteger(parsedSeatCount)) {
    return { ticket, error: 'The requested group seat count is invalid' };
  }
  const resized = resizeBuyerTicketGroup({
    ticket: added,
    ticketContractVersion,
    purchasedGroupID,
    seatCount: parsedSeatCount,
    createAttendee,
  });
  if (resized === added && added.selectedGroups?.[0]?.seatCount !== parsedSeatCount) {
    return { ticket, error: 'The requested group seat count is unavailable' };
  }
  return { ticket: resized, error: null };
};
